import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    ShieldCheck, Download, Trash2, Clock, CheckCircle2, XCircle, DatabaseZap,
} from "lucide-react";

/**
 * SaaS Ops queue for privacy requests + one-shot tenant backfill.
 *
 * • Data-export requests — GDPR/DPDP compliance queue. Ops fulfils by
 *   generating the export ZIP and emailing the requester.
 * • Data-deletion requests — same but for account removal.
 * • Backfill button — stamps `tenant_id="default"` on every legacy doc.
 *   Idempotent, safe to click multiple times.
 */

const STATUS_TONE = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    fulfilled: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rejected: "bg-rose-50 text-rose-800 border-rose-200",
};

export default function SaasOps() {
    const [data, setData] = useState({ counts: {}, data_exports: [], data_deletions: [] });
    const [loading, setLoading] = useState(true);
    const [decide, setDecide] = useState(null);   // {req, kind}
    const [reason, setReason] = useState("");
    const [status, setStatus] = useState("fulfilled");
    const [backfillBusy, setBackfillBusy] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/super-admin/privacy-requests")
            .then((r) => setData(r.data))
            .catch(() => setData({ counts: {}, data_exports: [], data_deletions: [] }))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const runBackfill = async (dry) => {
        setBackfillBusy(true);
        try {
            const { data: r } = await api.post(`/super-admin/backfill-tenant?dry_run=${dry ? "true" : "false"}`);
            toast.success(`${dry ? "[Dry-run] " : ""}Backfilled ${r.total_updated} docs across ${r.collections_touched} collections.`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBackfillBusy(false);
        }
    };

    const submitDecision = async () => {
        if (reason && reason.trim().length < 3) {
            toast.error("Reason too short.");
            return;
        }
        try {
            const path = decide.kind === "export"
                ? `/super-admin/privacy-requests/export/${decide.req.id}`
                : `/super-admin/privacy-requests/deletion/${decide.req.id}`;
            await api.patch(path, { status, reason: reason || null });
            toast.success(`Request ${status}`);
            setDecide(null); setReason(""); setStatus("fulfilled");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="saas-ops-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="eyebrow">SaaS Ops · Privacy queue</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Compliance desk.</h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Data-export and account-deletion requests, plus the one-shot tenant backfill
                        migration. Every action is audit-logged.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => runBackfill(true)} disabled={backfillBusy} data-testid="ops-backfill-dry">
                        <DatabaseZap size={13} className="mr-1.5" /> Dry-run backfill
                    </Button>
                    <Button onClick={() => runBackfill(false)} disabled={backfillBusy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="ops-backfill-run">
                        <DatabaseZap size={13} className="mr-1.5" /> Run backfill
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
                <MetricCard icon={Clock} label="Export requests pending" value={data.counts?.export_pending ?? 0} />
                <MetricCard icon={Clock} label="Deletion requests pending" value={data.counts?.delete_pending ?? 0} />
            </div>

            <Section title="Data exports" icon={Download} rows={data.data_exports}
                loading={loading} onDecide={(req) => setDecide({ req, kind: "export" })} />
            <div className="h-6" />
            <Section title="Data deletions" icon={Trash2} rows={data.data_deletions}
                loading={loading} onDecide={(req) => setDecide({ req, kind: "deletion" })} />

            <Dialog open={!!decide} onOpenChange={(o) => !o && setDecide(null)}>
                <DialogContent data-testid="ops-decide-dialog">
                    <DialogHeader>
                        <DialogTitle>Resolve {decide?.kind} request · {decide?.req?.user_email}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Label className="eyebrow">Decision</Label>
                        <div className="flex gap-2">
                            <Button variant={status === "fulfilled" ? "default" : "outline"} onClick={() => setStatus("fulfilled")} data-testid="ops-decision-fulfilled">
                                <CheckCircle2 size={13} className="mr-1.5" /> Fulfilled
                            </Button>
                            <Button variant={status === "rejected" ? "default" : "outline"} onClick={() => setStatus("rejected")} data-testid="ops-decision-rejected">
                                <XCircle size={13} className="mr-1.5" /> Rejected
                            </Button>
                        </div>
                        <Label className="eyebrow">Reason / note (optional)</Label>
                        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ops-decide-reason" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDecide(null)}>Cancel</Button>
                        <Button onClick={submitDecision} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="ops-decide-submit">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Section({ title, icon: Icon, rows, loading, onDecide }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-3">
                <Icon size={14} className="text-stone-500" />
                <h2 className="text-lg font-semibold text-stone-900">{title}</h2>
                <span className="ml-auto text-[12px] text-stone-500">{rows.length} total</span>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                <table className="dense-table w-full">
                    <thead>
                        <tr>
                            <th>Requested</th>
                            <th>User</th>
                            <th>Reason</th>
                            <th>Sections / notes</th>
                            <th>Status</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center text-stone-400 py-6">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={6} className="text-center text-stone-400 py-6">No {title.toLowerCase()} yet.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id}>
                                <td className="text-xs tabular-nums">{r.requested_at?.slice(0, 16)?.replace("T", " ")}</td>
                                <td>{r.user_email}</td>
                                <td className="max-w-[200px] truncate">{r.reason || "—"}</td>
                                <td className="text-[11px] text-stone-500 max-w-[200px] truncate">
                                    {Array.isArray(r.sections) ? r.sections.join(", ") : (r.resolution_reason || "—")}
                                </td>
                                <td><span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_TONE[r.status] || STATUS_TONE.pending}`}>{r.status}</span></td>
                                <td className="text-right">
                                    {r.status === "pending" && (
                                        <Button size="sm" variant="outline" onClick={() => onDecide(r)}>Resolve</Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function MetricCard({ icon: Icon, label, value }) {
    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-4 flex items-center gap-4">
            <span className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center"><Icon size={16} /></span>
            <div>
                <div className="eyebrow">{label}</div>
                <div className="text-3xl font-bold tabular-nums text-stone-900 mt-1">{value}</div>
            </div>
        </div>
    );
}
